// src/pages/help/PaymentReconciliationGuide.jsx
import React from 'react';
import { 
  ArrowLeft, CheckCircle, Clock, XCircle, 
  Mail, Smartphone, DollarSign, AlertCircle,
  Users, FileText, Bell, Calendar, Shield
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const PaymentReconciliationGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8 max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/help')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Back to Help Center
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Reconciliation</h1>
      <p className="text-gray-600 mb-8">
        Track and verify payments from Venmo, Zelle, Cash App, and other payment methods
      </p>

      {/* Quick Overview */}
      <div className="card bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200 p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white rounded-xl shadow-sm">
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">What is Payment Reconciliation?</h2>
            <p className="text-gray-700">
              When customers pay via Venmo, Zelle, Cash App, or cash, those payments don't automatically 
              sync with your invoices. Payment Reconciliation lets customers notify you when they've paid, 
              so you can verify and mark invoices as paid—all in one place.
            </p>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="card p-4 mb-8">
        <h3 className="font-semibold text-gray-900 mb-3">In This Guide</h3>
        <ul className="space-y-2 text-sm">
          <li>
            <a href="#how-it-works" className="text-primary-600 hover:text-primary-700">
              1. How It Works
            </a>
          </li>
          <li>
            <a href="#customer-experience" className="text-primary-600 hover:text-primary-700">
              2. The Customer Experience
            </a>
          </li>
          <li>
            <a href="#reviewing-payments" className="text-primary-600 hover:text-primary-700">
              3. Reviewing Pending Payments
            </a>
          </li>
          <li>
            <a href="#approving-rejecting" className="text-primary-600 hover:text-primary-700">
              4. Approving & Rejecting Payments
            </a>
          </li>
          <li>
            <a href="#notifications" className="text-primary-600 hover:text-primary-700">
              5. Notifications & Reminders
            </a>
          </li>
          <li>
            <a href="#best-practices" className="text-primary-600 hover:text-primary-700">
              6. Best Practices
            </a>
          </li>
        </ul>
      </div>

      {/* Section 1: How It Works */}
      <section id="how-it-works" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="text-primary-600" size={24} />
          1. How It Works
        </h2>
        
        <p className="text-gray-700 mb-4">
          Payment Reconciliation bridges the gap between receiving payments outside of credit cards 
          and keeping your invoices up to date.
        </p>

        <div className="card p-6 mb-4">
          <h3 className="font-semibold text-gray-900 mb-4">The Flow</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mail className="text-blue-600" size={24} />
              </div>
              <p className="font-medium text-gray-900 mb-1">1. Invoice Sent</p>
              <p className="text-sm text-gray-600">Customer receives invoice with payment options</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Smartphone className="text-green-600" size={24} />
              </div>
              <p className="font-medium text-gray-900 mb-1">2. Customer Pays</p>
              <p className="text-sm text-gray-600">Via Venmo, Zelle, Cash App, etc.</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Bell className="text-yellow-600" size={24} />
              </div>
              <p className="font-medium text-gray-900 mb-1">3. Customer Confirms</p>
              <p className="text-sm text-gray-600">Clicks "I've Paid" in the invoice email</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="text-purple-600" size={24} />
              </div>
              <p className="font-medium text-gray-900 mb-1">4. You Approve</p>
              <p className="text-sm text-gray-600">Verify and mark invoice as paid</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-blue-900">Why Not Auto-Approve?</p>
              <p className="text-sm text-blue-700">
                Unlike credit card payments that we can verify instantly, Venmo/Zelle payments 
                happen outside SuddyFlow. The confirmation step lets you verify the payment 
                actually arrived before marking the invoice as paid.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Customer Experience */}
      <section id="customer-experience" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="text-primary-600" size={24} />
          2. The Customer Experience
        </h2>
        
        <p className="text-gray-700 mb-4">
          Here's what your customers see when they receive an invoice:
        </p>

        <div className="card p-6 mb-4">
          <h3 className="font-semibold text-gray-900 mb-4">Invoice Email Contents</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium text-gray-900">Payment Options List</span>
                <p className="text-sm text-gray-600">Shows all the ways you accept payment (Venmo handle, Zelle info, etc.)</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium text-gray-900">"Pay Online" Button</span>
                <p className="text-sm text-gray-600">For credit card payments via Stripe (if enabled)</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium text-gray-900">"I've Already Paid" Button</span>
                <p className="text-sm text-gray-600">For customers who paid via Venmo, Zelle, cash, etc.</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="card p-6 bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-4">When Customer Clicks "I've Already Paid"</h3>
          <p className="text-gray-700 mb-3">They're taken to a simple form where they can:</p>
          <ol className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="font-medium text-primary-600">1.</span>
              <span>Select how they paid (Venmo, Zelle, Cash App, Cash, Check, etc.)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-medium text-primary-600">2.</span>
              <span>Confirm the amount paid</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-medium text-primary-600">3.</span>
              <span>Add a reference (Venmo @username, confirmation number, check #)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-medium text-primary-600">4.</span>
              <span>Add an optional note</span>
            </li>
          </ol>
        </div>
      </section>

      {/* Section 3: Reviewing Payments */}
      <section id="reviewing-payments" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="text-primary-600" size={24} />
          3. Reviewing Pending Payments
        </h2>
        
        <p className="text-gray-700 mb-4">
          When a customer confirms they've paid, you'll be notified and can review it in the 
          Reconcile Payments tab.
        </p>

        <div className="card p-6 mb-4">
          <h3 className="font-semibold text-gray-900 mb-4">Finding Pending Payments</h3>
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <div>
                <p className="font-medium text-gray-900">Go to Invoices</p>
                <p className="text-sm text-gray-600">You'll see a yellow badge if you have pending payments</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <div>
                <p className="font-medium text-gray-900">Click "Reconcile Payments" Tab</p>
                <p className="text-sm text-gray-600">See all pending payment confirmations in one place</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <div>
                <p className="font-medium text-gray-900">Review the Details</p>
                <p className="text-sm text-gray-600">Customer name, amount, payment method, reference, and any notes</p>
              </div>
            </li>
          </ol>
        </div>

        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Dashboard Summary Cards</h3>
          <p className="text-gray-700 mb-3">At the top of the Reconcile tab, you'll see:</p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="font-bold text-yellow-900 text-lg">Awaiting Review</p>
              <p className="text-sm text-yellow-700">Payments customers have confirmed but you haven't verified yet</p>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="font-bold text-red-900 text-lg">Unpaid Invoices</p>
              <p className="text-sm text-red-700">Total outstanding across all unpaid invoices</p>
            </div>
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-bold text-green-900 text-lg">Approved This Week</p>
              <p className="text-sm text-green-700">Payments you've verified in the last 7 days</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Approving & Rejecting */}
      <section id="approving-rejecting" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="text-primary-600" size={24} />
          4. Approving & Rejecting Payments
        </h2>

        <div className="space-y-4">
          {/* Approving */}
          <div className="card p-6 border-2 border-green-200 bg-green-50/30">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Approving a Payment</h3>
                <p className="text-gray-600 mb-3">
                  When you've verified the payment arrived in your Venmo, Zelle, bank account, etc.:
                </p>
                <ol className="text-sm text-gray-700 space-y-2 mb-3">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-green-700">1.</span>
                    <span>Click the green <strong>"Approve"</strong> button</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-green-700">2.</span>
                    <span>A payment record is automatically created</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-green-700">3.</span>
                    <span>The invoice is marked as Paid (or Partial if amount differs)</span>
                  </li>
                </ol>
                <div className="bg-green-100 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>Bulk Approve:</strong> Select multiple payments and approve them all at once!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Rejecting */}
          <div className="card p-6 border-2 border-red-200 bg-red-50/30">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="text-red-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Rejecting a Payment</h3>
                <p className="text-gray-600 mb-3">
                  If you can't find the payment or something doesn't match:
                </p>
                <ol className="text-sm text-gray-700 space-y-2 mb-3">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-red-700">1.</span>
                    <span>Click the <strong>"Reject"</strong> button (X icon)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-red-700">2.</span>
                    <span>Optionally add a reason (e.g., "Payment not received")</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-red-700">3.</span>
                    <span>The invoice remains unpaid</span>
                  </li>
                </ol>
                <div className="bg-red-100 rounded-lg p-3">
                  <p className="text-sm text-red-800">
                    <strong>Tip:</strong> Contact the customer to resolve payment issues. 
                    They may have sent to the wrong Venmo or made a typo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Notifications */}
      <section id="notifications" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="text-primary-600" size={24} />
          5. Notifications & Reminders
        </h2>
        
        <p className="text-gray-700 mb-4">
          Stay on top of pending payments with automatic notifications:
        </p>

        <div className="space-y-4">
          <div className="card p-4">
            <div className="flex items-start gap-3">
              <Mail className="text-blue-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-semibold text-gray-900">Instant Email Notification</h3>
                <p className="text-gray-600 text-sm">
                  When a customer confirms payment, you receive an email immediately with all 
                  the details and a link to review it.
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-start gap-3">
              <Calendar className="text-purple-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-semibold text-gray-900">Weekly Digest (Mondays)</h3>
                <p className="text-gray-600 text-sm">
                  Every Monday morning, you'll receive a summary email showing pending payments 
                  awaiting review, overdue invoices, and payments approved last week.
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-yellow-800 text-xs font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Badge in Navigation</h3>
                <p className="text-gray-600 text-sm">
                  The Invoices menu shows a yellow badge with the number of pending payments 
                  so you always know when something needs attention.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Best Practices */}
      <section id="best-practices" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="text-primary-600" size={24} />
          6. Best Practices
        </h2>

        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">✅ Review Payments Promptly</h3>
            <p className="text-gray-600 text-sm">
              Check your pending payments at least once a day. Customers appreciate knowing 
              their payment was received, and it keeps your records accurate.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">✅ Set Up Your Payment Methods</h3>
            <p className="text-gray-600 text-sm">
              Go to Settings → Payments and add your Venmo handle, Zelle info, Cash App tag, etc. 
              These will appear in your invoice emails so customers know where to send payment.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">✅ Ask for References</h3>
            <p className="text-gray-600 text-sm">
              Encourage customers to include their invoice number or name when sending Venmo/Zelle 
              payments. This makes matching payments to invoices much easier.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">✅ Use the Weekly Digest</h3>
            <p className="text-gray-600 text-sm">
              Don't ignore the Monday morning email! It's a great reminder to reconcile any 
              payments you might have missed during the busy week.
            </p>
          </div>

          <div className="card p-4 bg-amber-50 border-amber-200">
            <h3 className="font-semibold text-amber-900 mb-2">⚠️ Verify Before Approving</h3>
            <p className="text-amber-700 text-sm">
              Always check your Venmo/Zelle/bank account to confirm the payment actually arrived 
              before approving. Anyone can click "I've paid" – approval is your verification step.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>

        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">What if a customer pays but doesn't click "I've Paid"?</h3>
            <p className="text-gray-600 text-sm">
              No problem! You can still manually record the payment by clicking "Record Payment" 
              on the invoice in the Invoices tab, just like before.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Can customers submit multiple payment confirmations?</h3>
            <p className="text-gray-600 text-sm">
              If they already have a pending confirmation, clicking the link again will update 
              their existing submission rather than creating a duplicate.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">What if the amount doesn't match?</h3>
            <p className="text-gray-600 text-sm">
              You can still approve it. If the payment amount is less than the invoice total, 
              the invoice will be marked as "Partial" and the remaining balance will show as due.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Do credit card payments go through reconciliation?</h3>
            <p className="text-gray-600 text-sm">
              No! Credit card payments through Stripe are verified automatically and your invoice 
              is marked as paid instantly. Reconciliation is only for Venmo, Zelle, cash, etc.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-2">Ready to Reconcile?</h2>
        <p className="text-primary-100 mb-6">
          Check if you have any pending payment confirmations waiting for review.
        </p>
        <Link 
          to="/invoices?tab=reconcile"
          className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
        >
          <CheckCircle size={20} />
          Go to Reconcile Payments
        </Link>
      </div>
    </div>
  );
};

export default PaymentReconciliationGuide;